/**
 * Canonical IK-number rules — the single source of truth for both apps.
 *
 * A canonical IK number is exactly 9 digits, optionally marked:
 *   - `T` prefix — provisional/temporary IK (`"T123456789"`)
 *   - `X` suffix — withdrawn/inactive IK (`"123456789X"`)
 *
 * Both markers are uppercase and may appear together (`"T123456789X"`).
 * Everything that accepts, stores or compares an IK number goes through here so the
 * rule cannot drift between the SPA, the API and the XML import.
 */
/** Number of digits a well-formed IK number carries. */
export const IK_NUMBER_DIGIT_COUNT = 9;
/** Matches a canonical IK number. Only ever test *normalized* values against this. */
export const IK_NUMBER_REGEX = /^T?\d{9}X?$/;
/** User-facing explanation of the rule (German — matches the rest of the domain). */
export const IK_NUMBER_HINT = 'IK-Nummer muss aus 9 Ziffern bestehen — optional mit "T" davor (vorläufig) oder "X" dahinter (inaktiv)';
/**
 * Canonicalise an IK number without inventing or discarding digits.
 *
 * - trims, and drops every character that is not a digit or a `T`/`X` marker
 *   (spaces, `*`, and other stray characters seen in legacy rows)
 * - keeps at most one `T`, and only as the very first character
 * - keeps at most one `X`, and only as the very last character — so a value that
 *   already carried a lowercase `x` cannot collect a second marker
 * - uppercases both markers
 *
 * The digit count is deliberately left alone: an out-of-spec value stays out of spec
 * so {@link isValidIkNumber} can reject it instead of it being silently "repaired".
 *
 * @param value - raw IK number from a form, an API payload or an XML attribute
 * @returns the canonical form, or `null` when nothing usable remains
 */
export const normalizeIkNumber = (value) => {
    if (typeof value !== "string")
        return null;
    const cleaned = value.replace(/[^tTxX0-9]/g, "");
    if (cleaned.length === 0)
        return null;
    const prefix = /^[tT]/.test(cleaned) ? "T" : "";
    const suffix = /[xX]$/.test(cleaned) ? "X" : "";
    const digits = cleaned.replace(/\D/g, "");
    const normalized = prefix + digits + suffix;
    return normalized.length > 0 ? normalized : null;
};
/**
 * True when `value` is a well-formed IK number once normalized.
 * @param value - raw or canonical IK number
 */
export const isValidIkNumber = (value) => IK_NUMBER_REGEX.test(normalizeIkNumber(value) ?? "");
/**
 * Strip the markers, leaving the bare digits — for comparing a provisional or withdrawn
 * IK against the same number in another state. Returns `null` when there is nothing left.
 * @param value - raw or canonical IK number
 */
export const ikNumberDigits = (value) => {
    const digits = normalizeIkNumber(value)?.replace(/\D/g, "") ?? "";
    return digits.length > 0 ? digits : null;
};
